// ============================================================================
// ClientClaimPage — route /client/claim (public). Reads ?token= from the invite
// link the trainer sent, lets the client set a password (POST /api/client/claim
// via the client auth context), then drops them straight into their portal.
// Mobile-first, Pulse, EN + 繁中. Works fully with no prior session.
// ============================================================================
import { useState, type FormEvent } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Button, Card, TextField } from "../../components";
import { ApiError } from "../../api";
import { useClientAuth } from "../auth/ClientAuthContext";
import { ClientLanguageToggle } from "../ClientLanguageToggle";
import { ClientAuthBrand } from "./ClientAuthBrand";
import { CLIENT_HOME_PATH, CLIENT_LOGIN_PATH } from "../routes";

export function ClientClaimPage() {
  const { t } = useTranslation("clientPortal");
  const { claim, isAuthenticated, isLoading } = useClientAuth();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already signed in? Skip straight to the portal home.
  if (!isLoading && isAuthenticated) return <Navigate to={CLIENT_HOME_PATH} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError(t("claim.noToken"));
      return;
    }
    if (password.length < 8) {
      setError(t("claim.tooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("claim.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await claim({ token, password });
      // The context flips isAuthenticated; the redirect above takes over.
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("claim.invalidToken"));
      } else {
        setError(t("claim.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <ClientAuthBrand />
        <ClientLanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        <Card className="space-y-5">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
              {t("claim.kicker")}
            </p>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
              {t("claim.title")}
            </h1>
            <p className="mt-1.5 text-body-soft">{t("claim.blurb")}</p>
          </div>

          {!token && (
            <Banner tone="warning" title={t("claim.noToken")} />
          )}
          {error && <Banner tone="danger" title={error} />}

          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <TextField
              label={t("claim.password")}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={t("claim.passwordHint")}
              required
              disabled={submitting || !token}
            />
            <TextField
              label={t("claim.confirm")}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              disabled={submitting || !token}
            />
            <Button type="submit" block size="lg" disabled={submitting || !token}>
              {submitting ? t("claim.creating") : t("claim.submit")}
            </Button>
          </form>

          <p className="text-center text-sm text-body-soft">
            {t("claim.haveAccount")}{" "}
            <Link to={CLIENT_LOGIN_PATH} className="font-bold text-coral hover:underline">
              {t("claim.signIn")}
            </Link>
          </p>
        </Card>
      </main>
    </div>
  );
}
